import moment from "moment-timezone";
import {
  CalendarEventType,
  CalendarResource,
  SlotInfo as CalendarSlotInfo,
} from "../../../../../types/calendar";
import { BusinessInfo } from "../../../../../types/context";
import { DEFAULT_TIMEZONE } from "../../../../../utils/timeUtils";
import styles from "./Views.module.css";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  staff_photo_url?: string | null;
  avatar_url?: string | null;
  /** Salon-configured display order (Staff Settings "Edit Order"). */
  sort_order?: number;
  monday_start_time?: string;
  monday_end_time?: string;
  tuesday_start_time?: string;
  tuesday_end_time?: string;
  wednesday_start_time?: string;
  wednesday_end_time?: string;
  thursday_start_time?: string;
  thursday_end_time?: string;
  friday_start_time?: string;
  friday_end_time?: string;
  saturday_start_time?: string;
  saturday_end_time?: string;
  sunday_start_time?: string;
  sunday_end_time?: string;
}

/**
 * Returns "now" as a browser-local Date whose wall-clock equals the salon's
 * current wall-clock. react-big-calendar renders Dates in browser-local time
 * and momentLocalizer has no salon-tz default, so events are mapped this same
 * way (UTC -> salon tz -> formatted -> new Date). Feeding the calendar this as
 * `getNow`/`scrollToTime` keeps the red current-time indicator and the initial
 * scroll position aligned with the grid instead of the viewer's browser tz.
 */
export const getSalonNow = (timezone?: string): Date =>
  new Date(
    moment()
      .tz(timezone || DEFAULT_TIMEZONE)
      .format("YYYY-MM-DD HH:mm:ss"),
  );

/**
 * Build calendar resources from employees
 */
export const buildResources = (
  employees: Employee[],
  currentUserId?: string | null,
): CalendarResource[] => {
  // Respect the salon's configured staff display order (Staff Settings
  // "Edit Order"). Array.sort is stable, so members with an equal or absent
  // sort_order keep their incoming order.
  const ordered = [...employees].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  return ordered.map((employee) => {
    const name = `${employee.first_name} ${employee.last_name}`;
    return {
      id: employee.id,
      value: employee.id,
      title:
        currentUserId && employee.id === currentUserId ? `${name} (You)` : name,
      firstName: employee.first_name,
      lastName: employee.last_name,
      avatar: employee.staff_photo_url ?? employee.avatar_url ?? null,
      monday_start_time: employee.monday_start_time,
      monday_end_time: employee.monday_end_time,
      tuesday_start_time: employee.tuesday_start_time,
      tuesday_end_time: employee.tuesday_end_time,
      wednesday_start_time: employee.wednesday_start_time,
      wednesday_end_time: employee.wednesday_end_time,
      thursday_start_time: employee.thursday_start_time,
      thursday_end_time: employee.thursday_end_time,
      friday_start_time: employee.friday_start_time,
      friday_end_time: employee.friday_end_time,
      saturday_start_time: employee.saturday_start_time,
      saturday_end_time: employee.saturday_end_time,
      sunday_start_time: employee.sunday_start_time,
      sunday_end_time: employee.sunday_end_time,
    };
  });
};

/**
 * Drag/resize eligibility for calendar events. TimeBlocks are always
 * draggable; booked-service appointments are draggable except when they're
 * in a terminal status (Completed / No Show / Declined / Cancelled).
 */
const NON_DRAGGABLE_STATUSES = new Set([
  "Completed",
  "No Show",
  "Declined",
  "Cancelled",
]);

export const isEventDraggable = (event: CalendarEventType): boolean => {
  if (event.isTimeBlock) return true;
  const status = "status" in event ? event.status : undefined;
  if (!status) return true;
  return !NON_DRAGGABLE_STATUSES.has(status);
};

/**
 * Native hover-tooltip text for a calendar event (react-big-calendar's
 * `tooltipAccessor`). Defaults to the event title; Confirmed appointments get a
 * trailing " (CONFIRMED)" so the confirmed state is legible on hover as well as
 * from the flag chip, and "Any Available" bookings say so. Shared by the day
 * and week views.
 */
export const eventTooltipAccessor = (event: CalendarEventType): string => {
  const base = event.title || "";
  const status = "status" in event ? event.status : undefined;
  const confirmed = status === "Confirmed" ? `${base} (CONFIRMED)` : base;
  // The block itself no longer carries a "Booked via Any Available" line.
  return "bookedUsingAnyAvailable" in event && event.bookedUsingAnyAvailable
    ? `${confirmed} · Booked via Any Available`
    : confirmed;
};

/**
 * Slot prop getter - determines styling for time slots based on working hours
 */
/**
 * An ad-hoc hours override keyed by `${staffId}|${YYYY-MM-DD}`. Times are the
 * same "H:MM AM/PM" display strings the weekly `${day}_*_time` columns use, so
 * they slot straight into the same `moment(raw, "h:mm A")` comparison.
 */
export type HoursOverridesMap = Map<
  string,
  { start_time: string; end_time: string }
>;

/** Build the override lookup map from the salon-scoped overrides list. */
export const buildOverridesMap = (
  overrides: {
    staff_id: string;
    work_date: string;
    start_time: string | null;
    end_time: string | null;
  }[],
): HoursOverridesMap => {
  const map: HoursOverridesMap = new Map();
  for (const o of overrides) {
    // Both times are always present now (day-off was removed); guard anyway so
    // a malformed row never shades a slot with a missing bound.
    if (o.start_time && o.end_time) {
      map.set(`${o.staff_id}|${o.work_date}`, {
        start_time: o.start_time,
        end_time: o.end_time,
      });
    }
  }
  return map;
};

const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * A staff member's working hours on `date`, as the raw "H:MM AM/PM" strings:
 * an ad-hoc override for (staff, this exact date) wins, otherwise the weekly
 * `${day}_*_time` columns. Null when they have no hours that day. Shared by
 * the desktop slot shading and the mobile day grid so both read the same rule.
 *
 * Calendar Dates carry the salon wall-clock as browser-local time (see
 * getSalonNow), so the day comes from the native local getters.
 */
export const staffHoursOn = (
  resource: CalendarResource,
  date: Date,
  overridesMap?: HoursOverridesMap,
): { start_time: string; end_time: string } | null => {
  const workDate = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  const override = overridesMap?.get(`${resource.id}|${workDate}`);
  if (override) return override;

  const day = WEEKDAY_KEYS[date.getDay()];
  const start = resource[`${day}_start_time` as keyof CalendarResource];
  const end = resource[`${day}_end_time` as keyof CalendarResource];
  return start && end
    ? { start_time: start as string, end_time: end as string }
    : null;
};

export const createSlotPropGetter = (
  resources: CalendarResource[],
  overridesMap?: HoursOverridesMap,
) => {
  return (date: Date, resourceId?: string | number) => {
    if (!resourceId) {
      return {};
    }

    const resource = resources.find((r) => r.id === resourceId);
    if (!resource) {
      return {};
    }

    const slotTime = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    const hours = staffHoursOn(resource, date, overridesMap);

    // No hours that day: the closed (hatched) look.
    if (!hours) {
      return { className: styles.closedDay };
    }

    const startTime = moment(hours.start_time, "h:mm A").format("HH:mm");
    const endTime = moment(hours.end_time, "h:mm A").format("HH:mm");
    const isWorkingHours = slotTime >= startTime && slotTime < endTime;

    return {
      className: isWorkingHours ? styles.workingHours : styles.outsideWorkHours,
    };
  };
};

/**
 * Slot prop getter for salon business hours - used in Week view
 * Determines styling for time slots based on salon open/close times
 */
export const createSalonSlotPropGetter = (
  businessInfo: BusinessInfo | null,
) => {
  return (date: Date) => {
    if (!businessInfo) {
      return {};
    }

    const currentDay = moment(date).format("dddd").toLowerCase();
    const slotTime = moment(date).format("HH:mm");

    const openTimeRaw = businessInfo[
      `${currentDay}_open_time` as keyof BusinessInfo
    ] as string | undefined;
    const closeTimeRaw = businessInfo[
      `${currentDay}_end_time` as keyof BusinessInfo
    ] as string | undefined;

    // If no hours set for this day, salon is closed
    if (!openTimeRaw || !closeTimeRaw) {
      return { className: styles.closedDay };
    }

    const openTime = moment(openTimeRaw, "h:mm A").format("HH:mm");
    const closeTime = moment(closeTimeRaw, "h:mm A").format("HH:mm");
    const isOpenHours = slotTime >= openTime && slotTime < closeTime;

    return {
      className: isOpenHours ? styles.workingHours : styles.outsideWorkHours,
    };
  };
};

/**
 * Create slot selection handler
 */
export const createHandleSelectSlot = (
  onSelectSlot?: (slotInfo: CalendarSlotInfo) => void,
) => {
  return (slotInfo: any) => {
    if (onSelectSlot) {
      onSelectSlot({
        start: slotInfo.start,
        end: slotInfo.end,
        resourceId: slotInfo.resourceId as string | undefined,
        action: slotInfo.action as "select" | "click" | "doubleClick",
      });
    }
  };
};

/**
 * Common date format configuration for calendar views
 */
export const calendarFormats = {
  dayFormat: (date: Date, culture: string | undefined, localizer: any) => {
    const dayNumber = localizer.format(date, "D", culture);
    const dayName = localizer.format(date, "ddd", culture);
    return `${dayName} ${dayNumber}`;
  },
  eventTimeRangeStartFormat: (
    date: Date,
    culture: string | undefined,
    localizer: any,
  ) => {
    return localizer.format(date, "hh:mm A", culture) + " >> ";
  },
};

/**
 * Generate initials from employee name
 */
export const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
